import { RULES, COMMON_ACR, ICON_WORDS } from './rules';

// spoken version of a written text: context-aware (number + unit, ranges, signs, acronyms); visible text stays untouched
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const SPELL = 'SPELL';
let OVR = {};
try { OVR = JSON.parse(localStorage.getItem('giss_tts_rules') || '{}'); } catch { OVR = {}; }
const cache = new Map(), rx = {};
export const setOverrides = (o) => { OVR = o || {}; cache.clear(); try { localStorage.setItem('giss_tts_rules', JSON.stringify(OVR)); } catch { /* full */ } };
export const getOverrides = () => OVR;
export const acrMap = (lang) => ({ ...COMMON_ACR, ...(RULES[lang] || RULES.en).acr });
const spell = (t) => t.match(/\d+|./g).join(' ');
const EMO = { mic: /🎤|🎙️?/gu, eye: /👁️?/gu, gear: /⚙️?/gu, pin: /📍/gu, spk: /🔊|🔈|🔉/gu, warn: /⚠️?/gu, cam: /📷|📸/gu, refresh: /🔄|⟳/gu, swap: /⇄|⇋|⇵/gu, x: /[✕✖]/gu, ok: /[✓✔✅]/gu };

function regs(lang) {
  if (rx[lang]) return rx[lang];
  const R = RULES[lang], names = [...new Set(Object.values(R.units).flat())].filter((n) => n.length > 3).sort((a, b) => b.length - a.length).map(esc).join('|'), alt = Object.keys(R.units).sort((a, b) => b.length - a.length).map(esc).join('|');
  rx[lang] = {
    num: new RegExp(`(\\d+(?:[.,]\\d+)?)\\s?(${alt}|€|°C|°)(?:\\s?\\/\\s?(${alt}|\\p{L}+))?(?![\\p{L}\\d²³])`, 'gu'),
    par: new RegExp(`\\((${alt}|€)(?:\\/(${alt}))?\\)`, 'gu'),
    sup: new RegExp(`(?<![\\p{L}\\d])(mm²|cm²|m²|km²|m³|cm³)(?:\\/(${alt}))?(?![\\p{L}\\d])`, 'gu'),
    pair: new RegExp(`(?<![\\p{L}\\d])(${alt}|€)\\s?\\/\\s?(${alt}|\\p{L}+)(?![\\p{L}\\d])`, 'gu'),
    end: new RegExp(`(?<=(?:^|[\\s(])(?:±\\s?)?)(${alt})(?=\\))`, 'gu'),
    slash: new RegExp(`(\\p{L})\\s?\\/\\s?(${alt})(?![\\p{L}\\d²³])`, 'gu'),
    rng: new RegExp(`(\\d)\\s?-\\s?(?=\\d+(?:[.,]\\d+)?\\s?(?:${alt}|%|‰|€|°)(?![\\p{L}\\d]))`, 'gu'),
    hyph: new RegExp(`(\\d)-(${alt})(?=[-\\s])`, 'gu'),
    ctx: new RegExp(`(?<=(?:(?:^|\\s)(?:pro|je|per|in|al|por|par|en|em|a|à|oder|or|o|ou|und|and|e|y|et)|${names}),?\\s)(${alt})(?=[\\s,.;:)]|$)`, 'gu'),
    abbr: [...R.abbr].sort((a, b) => b[0].length - a[0].length).map(([w, s, c]) => [new RegExp(`${c === '<n' ? '(?<=\\d\\s?)' : '(?<=^|[\\s(„"«-])'}${esc(w)}(?=${c === 'n>' ? '\\s?\\d' : '[\\s,;:)!?]|$'})`, 'gu'), s]),
  };
  return rx[lang];
}

export function spoken(text, lang) {
  const R = RULES[lang];
  if (!R || !text) return text || '';
  const key = `${lang}|${text}`;
  if (cache.has(key)) return cache.get(key);
  const W = R.words, X = regs(lang), keep = [];
  const prot = (m) => { keep.push(m); return `\u0001${keep.length - 1}\u0002`; };
  const uName = (u, n) => (!R.units[u] && !['a', '€', '°C', '°'].includes(u) ? u : u === 'a' ? W.year : u === '€' ? W.eur : u === '°C' ? W.degC : u === '°' ? (n === '1' ? W.deg1 : W.deg) : (R.units[u] || [u, u])[n === '1' ? 0 : 1]);
  const fixNum = (n) => (R.dec === ',' ? n.replace(/^(\d+)\.(\d{1,2})$/, '$1,$2') : n.replace(/^(\d+),(\d{1,2})$/, '$1.$2'));
  let s = ` ${text} `;
  // untouchable: links, mail, licence / article codes, dates
  // plus codes (8QJ28FVG+X2): letters and digits one by one
  s = s.replace(/\b([A-Z0-9]{2,8})\+([A-Z0-9]{2,3})\b/g, (m, a, b) => prot(`${a.split('').join(' ')} ${W.plus} ${b.split('').join(' ')}`));
  // file names / extensions (.giss, .json)
  s = s.replace(/(?<=^|[\s(\p{L}])\.(giss|json|geojson|dxf|dwg|pdf|csv|kmz|kml|gpx|xlsx|zip|png|jpg|mp3)\b/giu, (m, e) => ` ${W.dot} ${e.toLowerCase()}`);
  s = s.replace(/https?:\/\/\S+|[\w.+-]+@[\w-]+\.[\w.]+|\b[A-Z]{2,}(?:-[A-Z0-9#]+){2,}\b|\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}\.\d{1,2}\.\d{2,4}\b/g, prot);
  // owner corrections first (exact written form)
  (OVR[lang] || []).slice().sort((a, b) => b.w.length - a.w.length).forEach(({ w, s: sp }) => { s = s.replace(new RegExp(`(?<![\\p{L}\\d])${esc(w)}(?![\\p{L}\\d])`, 'gu'), () => prot(sp)); });
  X.abbr.forEach(([re, sp]) => { s = s.replace(re, () => prot(sp)); });
  // icons with meaning -> words, decorative pictographs -> silence
  const I = ICON_WORDS[lang];
  Object.entries(EMO).forEach(([k, re]) => { s = s.replace(re, ` ${I[k]} `); });
  s = s.replace(/⚡\uFE0F?/g, ` ${W.bolt} `).replace(/◀\uFE0F?/g, ` ${W.larr} `).replace(/▶\uFE0F?/g, ` ${W.rarr} `);
  s = s.replace(/\p{Extended_Pictographic}\uFE0F?|[\uFE0F\u00AD]/gu, ' ').replace(/[„“”"«»]/g, ' ');
  // scale 1:500 before anything reads it as a time
  // coordinates and long decimals written with a point
  if (R.dec === ',') s = s.replace(/(?<![\d.,])(\d{1,3})\.(\d{3,})(?!\d|\.\d)/g, '$1,$2');
  else s = s.replace(/(?<![\d.,])(\d{1,3}),(\d{4,})(?!\d|,\d)/g, '$1.$2');
  s = s.replace(/(?<![\d:])1\s?:\s?(\d{3,})(?![\d:])/g, (m, b) => `1 ${W.to} ${b}`);
  // numbers with units (incl. composite units like m³/h, €/m³), units in brackets, superscript units
  s = s.replace(X.rng, `$1 ${W.range} `);
  s = s.replace(X.hyph, (m, n, u) => `${n}-${uName(u, n)}`);
  s = s.replace(X.slash, (m, w, u) => `${w} ${W.per} ${uName(u, '1')}`);
  s = s.replace(X.num, (m, n, u1, u2) => `${fixNum(n)} ${uName(u1, n)}${u2 ? ` ${W.per} ${uName(u2, '1')}` : ''}`);
  s = s.replace(/€\s?(\d+(?:[.,]\d+)?)/g, (m, n) => `${fixNum(n)} ${W.eur}`);
  s = s.replace(X.par, (m, u1, u2) => `(${uName(u1, '2')}${u2 ? ` ${W.per} ${uName(u2, '1')}` : ''})`);
  s = s.replace(X.sup, (m, u1, u2) => `${uName(u1, '2')}${u2 ? ` ${W.per} ${uName(u2, '1')}` : ''}`);
  s = s.replace(X.end, (m, u) => uName(u, '2'));
  s = s.replace(X.pair, (m, u1, u2) => `${uName(u1, '2')} ${W.per} ${uName(u2, '1')}`);
  // unit words after "pro / per / in …" and in lists of units ("pro m³, t oder m²")
  for (let i = 0; i < 3; i += 1) s = s.replace(X.ctx, (m, u) => (u.length > 1 || /^[mtlhs]$/.test(u) ? uName(u, '2') : m));
  // signs and maths in context
  s = s
    .replace(/Ø\s?/g, ` ${W.dia} `)
    .replace(/Δ\s?/g, ' Delta ')
    .replace(/[μµ](?![\p{L}])/gu, ` ${W.mu} `)
    .replace(/(?<![\p{L}])(\p{Lu})\s?→\s?(\p{Lu})(?![\p{L}])/gu, `${W.from} $1 ${W.to2} $2`)
    .replace(/([\p{L}\d)])\s?·\s?(?=[\p{L}\d(√])/gu, `$1 ${W.times} `)
    .replace(/±\s?/g, ` ${W.pm} `)
    .replace(/(\d)(x?)\s?[–—]\s?(?=[−]?\d)/g, `$1$2 ${W.range} `)
    .replace(/(\d)\+(?=[\s,.;)]|$)/g, `$1 ${W.newer}`)
    .replace(/(\d)x(?=[\s,.;)]|$)/g, `$1${W.fold}`)
    .replace(/(^|[\s(=:/])[−-](?=\d)/g, `$1${W.minus} `)
    .replace(/−(?=\s?\d)/g, ` ${W.minus} `)
    .replace(/(^|[\s(])\+(?=\d)/g, `$1${W.plus} `)
    .replace(/\s?%/g, ` ${W.pct}`)
    .replace(/\s?‰/g, ` ${W.pml}`)
    .replace(/\+\/[−-]/g, ` ${W.pm} `)
    .replace(/(^|[\s(])\+(?=[\s,])/g, `$1${W.plus}`)
    .replace(/(^|[\s(])[−](?=[\s,])/g, `$1${W.minus}`)
    .replace(/<(\p{L}[\p{L}\s-]*)>/gu, '$1')
    .replace(/(\d)\/(?=\d)/g, `$1 ${W.of} `)
    .replace(/(\p{L})\/(?=\d)/gu, `$1 ${W.div} `)
    .replace(/(\d)\s?[×x]\s?(?=\d)/g, `$1 ${W.times} `)
    .replace(/(\p{L})\s×\s(?=\p{L})/gu, `$1 ${W.times} `)
    .replace(/×/g, ` ${I.x} `)
    .replace(/≈\s?/g, ` ${W.approx} `).replace(/≤\s?/g, ` ${W.le} `).replace(/≥\s?/g, ` ${W.ge} `)
    .replace(/<\s?(?=\d)/g, ` ${W.lt} `).replace(/>\s?(?=\d)/g, ` ${W.gt} `)
    .replace(/\s=\s/g, ` ${W.eq} `)
    .replace(/√\s?/g, ` ${W.root} `)
    .replace(/#\s?(?=\d)/g, ` ${W.num} `)
    .replace(/\s*(→|➔|->|=>|⇒|›|►|▸)\s*/g, `${W.then} `)
    .replace(/\s(>|»)\s/g, `${W.then} `)
    .replace(/[⋮⋯]\s*(menu|menü|menú)?/giu, ` ${I.menu} `)
    .replace(/(^|[\s(„“"'«])[?？](?=[\s)“”"'».,;:!]|$)/gu, `$1 ${I.q} `)
    .replace(/\s\+\s/g, ` ${W.and} `)
    .replace(/\s?&\s?/g, ` ${W.and} `)
    .replace(/@/g, ` ${W.at} `)
    .replace(/€/g, ` ${W.eur} `)
    .replace(/(\p{L}-?)\/(?=\p{L})/gu, `$1 ${W.or} `)
    .replace(/\s\/\s/g, ', ')
    .replace(/\s[–—]\s/g, ', ')
    .replace(/\s*[·|]\s*/g, ', ')
    .replace(/[_{}]/g, ' ')
    .replace(/\s*…/g, '.')
    .replace(/[„“”"«»◄▼▾◂]/g, ' ');
  // acronyms: spelled or spoken as a word; shouted words in capitals are read normally
  const A = acrMap(lang);
  s = s.replace(/(?<![\p{L}\d\u0001])([A-Za-zÄÖÜ][A-Za-z0-9ÄÖÜ]*)(?![\p{L}\d])/gu, (m) => {
    const v = A[m];
    if (v) return v === SPELL ? spell(m) : v;
    if (/^[A-ZÄÖÜ]{5,}$/.test(m)) return m[0] + m.slice(1).toLowerCase();
    return m;
  });
  if (lang === 'it') s = s.replace(/\bal (?=[aeiouhAEIOU])/g, 'all’');
  s = s.replace(/\u0001(\d+)\u0002/g, (m, i) => keep[+i]).replace(/\s+([,.;:!?)])/g, '$1').replace(/\(\s+/g, '(').replace(/,\s*,/g, ',').replace(/\s+/g, ' ').trim();
  if (cache.size > 3000) cache.clear();
  cache.set(key, s);
  return s;
}

// open points of one text: unknown abbreviations, unclear dotted abbreviations, symbols left in the spoken version
export function auditText(text, lang) {
  const R = RULES[lang], A = acrMap(lang), out = [];
  if (!R || !text) return out;
  const known = new Set([...R.abbr.map((a) => a[0]), ...(OVR[lang] || []).map((o) => o.w)]);
  let rest = text;
  known.forEach((k) => { rest = rest.replace(new RegExp(`(?<![\\p{L}])${esc(k)}`, 'gu'), ' '); });
  (rest.match(/(?<![\p{L}\d])[A-Za-zÄÖÜ]*[A-ZÄÖÜ][A-Za-zÄÖÜ]*[A-ZÄÖÜ][A-Za-z0-9ÄÖÜ]*(?![\p{L}\d])/gu) || []).forEach((t) => {
    if (!A[t] && !known.has(t) && !/^[A-ZÄÖÜ]{5,}$/.test(t) && !/^[A-Z][a-z]+[A-Z][a-z]+/.test(t)) out.push({ k: 'acr', t });
  });
  (rest.match(/(?<![\p{L}.])\p{Ll}{1,4}\.(?=\s\p{Ll})/gu) || []).forEach((t) => { if (!known.has(t)) out.push({ k: 'abbr', t }); });
  (spoken(text, lang).match(/[^\p{L}\p{N}\s.,;:!?()'’‘\-–‑¿¡]/gu) || []).forEach((t) => out.push({ k: 'sym', t }));
  return out;
}
